import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Sparkles, X, ChevronRight, MessageSquare, Activity, ClipboardCheck, Timer } from 'lucide-react';
import { trackQuickstartCardClick } from '../../hooks/useAnalytics';

// 3 most popular first-touch flows for the /connect marketplace.
// `destId` maps to the CATALOG.id in IntegrationsMarketplace.js, which we
// pass back to the parent via `onStart` so the existing Connect dialog opens
// with the destination pre-selected (zero new backend code).
const FLOWS = [
  {
    destId: 'slack',
    Icon: MessageSquare,
    badge: 'Most popular',
    title: 'Cost alerts in Slack',
    subtitle: 'Anomalies + weekly digest → your #finance channel',
    steps: [
      'Open api.slack.com/apps → Incoming Webhooks',
      'Paste the webhook URL into the next dialog',
      'Send a test event → confirm the ping lands',
    ],
    accent: 'from-violet-500 to-fuchsia-500',
    shadow: 'shadow-[0_0_30px_-8px_rgba(168,85,247,0.55)]',
  },
  {
    destId: 'jira',
    Icon: ClipboardCheck,
    badge: 'Enterprise favorite',
    title: 'Auto-open Jira tickets',
    subtitle: 'Cost overruns become tracked tickets on your board',
    steps: [
      'Jira → Project Settings → Automation → Incoming Webhook',
      'Paste the trigger URL into the dialog',
      'Set anomaly events → CARTIE files the ticket',
    ],
    accent: 'from-sky-500 to-blue-600',
    shadow: 'shadow-[0_0_30px_-8px_rgba(59,130,246,0.55)]',
  },
  {
    destId: 'datadog',
    Icon: Activity,
    badge: 'DevOps standard',
    title: 'Datadog cost events',
    subtitle: 'Spend anomalies on the same dashboard as latency',
    steps: [
      'Datadog → Integrations → Webhooks → New',
      'Paste the Datadog webhook URL in CARTIE',
      'Anomalies now overlay your perf metrics',
    ],
    accent: 'from-emerald-500 to-teal-500',
    shadow: 'shadow-[0_0_30px_-8px_rgba(16,185,129,0.55)]',
  },
];

const DISMISS_KEY = 'cartie-quickstart-strip-dismissed-v1';

const QuickstartStrip = ({ catalog = [], installed = [], onStart }) => {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  );

  if (dismissed) return null;
  // Auto-hide once the user has at least one integration — they don't need onboarding anymore.
  if ((installed || []).length >= 1) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleStart = (destId) => {
    trackQuickstartCardClick(destId);
    const dest = (catalog || []).find((c) => c.id === destId);
    if (dest && typeof onStart === 'function') onStart(dest);
  };

  return (
    <section
      className="relative rounded-2xl border border-emerald-500/20 bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 p-6 overflow-hidden"
      data-testid="quickstart-strip"
    >
      {/* Glow blobs */}
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-violet-500/8 blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] font-bold tracking-widest">
              <Sparkles className="w-3 h-3 mr-1" /> QUICKSTART
            </Badge>
            <Badge variant="outline" className="text-[10px] font-bold tracking-widest border-slate-700 text-slate-300">
              <Timer className="w-3 h-3 mr-1" /> 60 SECONDS
            </Badge>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-white">
            Get your first alert flowing in under a minute
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Pick the destination your team already lives in. Paste a webhook, send a test, done.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded-md hover:bg-slate-800/60"
          aria-label="Dismiss quickstart"
          data-testid="quickstart-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Cards */}
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4">
        {FLOWS.map((flow) => {
          const Icon = flow.Icon;
          return (
            <Card
              key={flow.destId}
              className="group bg-slate-900/60 border-slate-700/60 hover:border-emerald-500/40 transition-all hover:-translate-y-0.5 cursor-pointer backdrop-blur-sm"
              data-testid={`quickstart-card-${flow.destId}`}
              onClick={() => handleStart(flow.destId)}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={`w-11 h-11 rounded-xl bg-gradient-to-br ${flow.accent} ${flow.shadow} flex items-center justify-center`}
                  >
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.4} />
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[10px] font-bold tracking-wider border-slate-700 text-slate-400 group-hover:border-emerald-500/40 group-hover:text-emerald-300 transition-colors"
                  >
                    {flow.badge}
                  </Badge>
                </div>
                <h3 className="font-bold text-white text-base mb-1">{flow.title}</h3>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">{flow.subtitle}</p>

                <ol className="space-y-2 mb-5">
                  {flow.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                      <span
                        className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${flow.accent} text-white text-[10px] font-bold flex items-center justify-center mt-px`}
                      >
                        {idx + 1}
                      </span>
                      <span className="leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-between bg-slate-950/40 border-slate-700 text-slate-100 hover:bg-emerald-500/10 hover:border-emerald-500/40 hover:text-emerald-200"
                  data-testid={`quickstart-start-${flow.destId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStart(flow.destId);
                  }}
                >
                  <span>Start</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

export default QuickstartStrip;
